<template>
  <v-dialog
    v-model="dialog"
    persistent
    hide-overlay
    scrollable
    transition="slide-x-reverse-transition"
    content-class="label-sheet"
  >
    <v-card class="label-sheet-card">
      <v-card-title class="label-sheet__title">
        <span class="label-sheet__title-icon">
          <v-icon color="white">mdi-tag-multiple-outline</v-icon>
        </span>
        <span>
          <span class="label-sheet__title-text d-block">Labels</span>
          <span class="label-sheet__subtitle d-block">Create and organize your asset labels</span>
        </span>
        <v-spacer></v-spacer>
        <v-btn icon small dark @click="toggleDialog">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="label-sheet__body">
        <v-alert v-if="!!error" text dense type="error" icon="mdi-alert-circle" class="mb-3">{{ error }}</v-alert>

        <div class="label-sheet__create">
          <v-text-field
            v-model="name"
            label="New label"
            placeholder="e.g. Finance"
            outlined
            dense
            hide-details
            maxlength="50"
            @keyup.enter="save"
          ></v-text-field>
          <v-btn color="primary" depressed :disabled="!canAdd" @click="save">
            <v-icon small left>mdi-plus</v-icon>Add
          </v-btn>
        </div>

        <v-text-field
          v-model="search"
          label="Search labels"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="mb-3"
        ></v-text-field>

        <v-skeleton-loader v-if="isLoading" type="list-item@4" class="mx-auto"></v-skeleton-loader>

        <div v-else-if="!filteredLabels.length" class="label-sheet__empty">
          <v-icon size="34" color="rgba(102, 63, 92, 0.35)">mdi-tag-off-outline</v-icon>
          <span class="label-sheet__empty-text">{{ search ? "No labels match your search" : "No labels yet" }}</span>
        </div>

        <div v-else class="label-sheet__list">
          <div v-for="label in filteredLabels" :key="label.Id" class="label-row">
            <template v-if="editingId === label.Id">
              <v-text-field
                v-model="editName"
                outlined
                dense
                hide-details
                autofocus
                maxlength="50"
                class="label-row__input"
                @keyup.enter="update(label.Id)"
                @keyup.esc="cancelEdit"
              ></v-text-field>
              <v-btn icon small color="primary" :disabled="!editName || !editName.trim()" @click="update(label.Id)">
                <v-icon small>mdi-check</v-icon>
              </v-btn>
              <v-btn icon small @click="cancelEdit">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </template>

            <template v-else-if="confirmId === label.Id">
              <span class="label-row__confirm">Delete "{{ label.Name }}"?</span>
              <v-btn text small color="error" @click="remove(label.Id)">Delete</v-btn>
              <v-btn text small color="grey darken-1" @click="confirmId = null">Cancel</v-btn>
            </template>

            <template v-else>
              <span class="label-row__dot"></span>
              <span class="label-row__name">{{ label.Name }}</span>
              <v-btn icon small color="grey darken-1" @click="startEdit(label)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn icon small color="grey darken-1" @click="confirmId = label.Id">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="label-sheet__actions">
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="toggleDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { FETCH_LABELS, CREATE_LABEL, UPDATE_LABEL, DELETE_LABEL, TOGGLE_LABEL_DIALOG } from "../../store/modules/asset";

export default {
  data: () => ({
    search: "",
    name: "",
    editName: "",
    editingId: null,
    confirmId: null,
  }),
  computed: {
    ...mapState("asset", {
      labels: (state) => state.labels,
      isLoading: (state) => state.labelIsLoading,
      dialog: (state) => state.labelDialog,
      error: (state) => state.createLabel.error || state.updateLabel.error || state.deleteLabel.error,
    }),
    canAdd() {
      return !!this.name && !!this.name.trim();
    },
    filteredLabels() {
      const term = (this.search || "").trim().toLowerCase();
      if (!term) return this.labels;
      return this.labels.filter((label) => (label.Name || "").toLowerCase().includes(term));
    },
  },
  watch: {
    dialog(value) {
      if (value) {
        this.fetchLabels();
      } else {
        this.name = "";
        this.search = "";
        this.cancelEdit();
        this.confirmId = null;
      }
    },
  },
  mounted() {
    this.fetchLabels();
  },
  methods: {
    ...mapActions("asset", {
      fetchLabels: FETCH_LABELS,
      createLabel: CREATE_LABEL,
      updateLabel: UPDATE_LABEL,
      deleteLabel: DELETE_LABEL,
      toggleDialog: TOGGLE_LABEL_DIALOG,
    }),
    save() {
      if (!this.canAdd) return;

      this.createLabel({ Name: this.name.trim() });
      this.name = "";
    },
    startEdit(label) {
      this.confirmId = null;
      this.editingId = label.Id;
      this.editName = label.Name;
    },
    cancelEdit() {
      this.editingId = null;
      this.editName = "";
    },
    update(id) {
      if (!this.editName || !this.editName.trim()) return;

      this.updateLabel({ labelId: id, label: { Name: this.editName.trim() } });
      this.cancelEdit();
    },
    remove(id) {
      this.confirmId = null;
      this.deleteLabel(id);
    },
  },
};
</script>
